import { useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CircleCheckBig, Home, Clock, FileCheck, Mail } from 'lucide-react';
import OnboardingLayout from '@/components/OnboardingLayout';
import { tokenStorage } from '@/utils/token-storage';
import { authUtils } from '@/utils/auth';

export default function OnboardingSuccess() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Get account type from URL params or location state
  const accountType = searchParams.get('type') || location.state?.accountType || 'company';
  const isContractor = accountType === 'architect' || accountType === 'subcontractor';

  // Get reference number from API response
  const referenceNumber = location.state?.referenceNumber || null;

  // Set tokens and user data when component mounts
  useEffect(() => {
    const { tokens, user } = location.state || {};

    // Store tokens
    if (tokens?.access_token && tokens?.refresh_token) {
      tokenStorage.setTokens(tokens.access_token, tokens.refresh_token);
    }

    // Store user data
    if (user) {
      authUtils.setUserData(user);
    }
  }, [location.state]);

  return (
    <OnboardingLayout
      currentStep={isContractor ? 7 : 5}
      totalSteps={isContractor ? 7 : 5}
      hideSteps={true}
      hideNavigation={true}
    >
      <div className="w-[85%] mx-auto">
        <div className="bg-card rounded-lg p-12 space-y-10 text-center">
          {/* Success Icon */}
          <div className="flex justify-center">
            <CircleCheckBig className="w-20 h-20 text-success" />
          </div>

          {/* Success Messages */}
          <div className="space-y-6">
            <h1 className="text-5xl font-semibold text-primary">
              Application Submitted Successfully!
            </h1>
            <p className="text-2xl text-muted-foreground">
              Thank you for completing your onboarding with LarkPM
            </p>
          </div>

          {/* What's Next Card */}
          <Card className=" bg-green-50 border-green-200">
            <CardHeader>
              <h2 className="text-3xl font-semibold text-success text-left">
                {isContractor ? 'What happens next?' : 'What\'s Next?'}
              </h2>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Next Steps List */}
              <div className="space-y-6 text-left">
                {isContractor ? (
                  <>
                    {/* Review Process */}
                    <div className="flex items-start gap-4">
                      <Clock className="w-6 h-6 text-success mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="text-xl font-bold tracking-tight text-success">
                          Review Process
                        </h3>
                        <p className="text-base text-success">
                          Our team will review your application within 2-3 business days
                        </p>
                      </div>
                    </div>

                    {/* Document Verification */}
                    <div className="flex items-start gap-4">
                      <FileCheck className="w-6 h-6 text-success mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="text-xl font-bold tracking-tight text-success">
                          Document Verification
                        </h3>
                        <p className="text-base text-success">
                          We'll verify your insurance policies and business licenses
                        </p>
                      </div>
                    </div>

                    {/* Account Activation */}
                    <div className="flex items-start gap-4">
                      <Mail className="w-6 h-6 text-success mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="text-xl font-bold tracking-tight text-success">
                          Account Activation
                        </h3>
                        <p className="text-base text-success">
                          You'll receive an email with your account credentials and next steps
                        </p>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Access Dashboard - Company only */}
                    <div className="flex items-start gap-4">
                      <CircleCheckBig className="w-6 h-6 text-success mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="text-xl font-bold tracking-tight text-success">
                          Access Your Dashboard
                        </h3>
                        <p className="text-base text-success">
                          Start managing your construction projects and clients
                        </p>
                      </div>
                    </div>

                    {/* Team Invitations - Company only */}
                    <div className="flex items-start gap-4">
                      <CircleCheckBig className="w-6 h-6 text-success mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="text-xl font-bold tracking-tight text-success">
                          Team Invitations
                        </h3>
                        <p className="text-base text-success">
                          Your team members will receive setup instructions via email
                        </p>
                      </div>
                    </div>

                    {/* Configure Settings - Company only */}
                    <div className="flex items-start gap-4">
                      <CircleCheckBig className="w-6 h-6 text-success mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="text-xl font-bold tracking-tight text-success">
                          Configure Settings
                        </h3>
                        <p className="text-base text-success">
                          Customize your CRM settings and preferences
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Reference Number for Contractors */}
          {isContractor && referenceNumber && (
            <Card className=" bg-blue-50 border-blue-200 max-w mx-auto">
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold tracking-tight text-blue-800 mb-2">Reference Number: {referenceNumber}</h3>
                <p className="text-md text-blue-600">Please save this reference number for your records</p>
              </CardContent>
            </Card>
          )}

          {/* Contact Info for Contractors */}
          {isContractor && (
            <div className="text-center text-muted-foreground">
              <p>We'll send updates to your email address. If you have any questions, please contact our</p>
              <p>support team at <span className="text-blue-600 font-medium">support@larkpm.com</span></p>
            </div>
          )}

          {/* Dashboard Button */}
          <div className="flex justify-center pt-4">
            <Button
              onClick={() => navigate('/dashboard')}
              className="bg-primary hover:bg-primary/90 text-primary-foreground flex items-center gap-2 px-8 py-6 text-lg"
            >
              <Home className="w-5 h-5" />
              Go to Dashboard
            </Button>

          </div>
        </div>
      </div>
    </OnboardingLayout>
  );
}
